"use client";

import { Award, Backpack, FileCheck, Flag, Mountain, Radio, Wind } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheInfinixValues() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: stepsRef,
    isVisible: stepsVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: includedRef,
    isVisible: includedVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    number: "01",
    title: "DATA AUDIT",
    Icon: FileCheck,
    description: "We conduct a comprehensive assessment of your existing data infrastructure and identify key integration points."
  }, {
    number: "02",
    title: "SYSTEM MAPPING",
    Icon: Backpack,
    description: "Our architects design a robust framework for your data flow, ensuring security, scalability, and performance."
  }, {
    number: "03",
    title: "INTEGRATION",
    Icon: Wind,
    description: "Seamlessly connecting your disparate systems into a unified, high-performance intelligence ecosystem."
  }, {
    number: "04",
    title: "AUTOMATION",
    Icon: Mountain,
    description: "Deploying intelligent workflows that eliminate manual bottlenecks and accelerate your business processes."
  }, {
    number: "05",
    title: "VALIDATION",
    Icon: Flag,
    description: "Rigorous testing protocols to ensure data integrity and system reliability under peak operational loads."
  }, {
    number: "06",
    title: "OPTIMIZATION",
    Icon: Award,
    description: "Continuous monitoring and refinement to maximize your ROI and maintain a competitive edge."
  }];
  const included = ["Full system architecture audit", "Custom automation workflows", "Real-time analytics dashboards", "Enterprise-grade security protocols", "Dedicated technical support", "Performance optimization reports"];
  return <section data-section-id="2290" id="the-infinix-values" className="dark text-foreground py-32 px-6 lg:px-12 overflow-hidden bg-accent">
      <div className="max-w-[1400px] mx-auto">
        {}
        <div ref={headerRef} className={`mb-20 transition-all duration-700 ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-secondary" />
            <p className="font-mono text-xs tracking-[0.3em] text-secondary uppercase">
              [ Methodology ]
            </p>
          </div>
          <h2 className="font-default font-bold text-5xl md:text-7xl lg:text-8xl uppercase tracking-tight text-foreground mb-6 leading-[0.9]">
            Our Core Process
          </h2>
          <p className="font-default text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed">
            A six-stage strategic framework engineered for actionable intelligence. Every phase is measured, optimized, and aligned with your business goals.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-12">
          {}
          <div ref={stepsRef} className="relative">
            {}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-px -translate-y-1/2 pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(to right, var(--secondary) 0, var(--secondary) 8px, transparent 8px, transparent 16px)",
            zIndex: 0
          }} aria-hidden="true" />

            {}
            <div className="lg:hidden absolute top-0 bottom-0 left-8 w-px pointer-events-none" style={{
            backgroundImage: "repeating-linear-gradient(to bottom, var(--secondary) 0, var(--secondary) 8px, transparent 8px, transparent 16px)",
            zIndex: 0
          }} aria-hidden="true" />

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 relative z-10">
              {steps.map((step, i) => {
              const Icon = step.Icon;
              return <Card data-index={i} key={step.number} className={`relative bg-card/60 backdrop-blur-md border border-border shadow-lg py-8 px-6 gap-5 transition-all duration-700 ${stepsVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`} style={{
                transitionDelay: `${i * 100}ms`
              }}>
                    {}
                    <div className="hidden lg:block absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-secondary" aria-hidden="true" />
                    <div className="lg:hidden absolute top-10 -left-[1.65rem] w-3 h-3 bg-secondary" aria-hidden="true" />

                    {}
                    <div className="font-mono text-5xl md:text-6xl font-bold text-secondary leading-none">
                      {step.number}
                    </div>

                    {}
                    <div className="flex items-center justify-start">
                      <Icon className="size-9 text-foreground" strokeWidth={1.5} aria-hidden="true" />
                    </div>

                    {}
                    <h3 className="font-default font-bold text-xl uppercase tracking-wide text-foreground">
                      {step.title}
                    </h3>

                    {}
                    <p className="font-default text-sm leading-relaxed text-muted-foreground">
                      {step.description}
                    </p>

                    {}
                    <div className="h-px w-12 bg-secondary/60 mt-2" />
                  </Card>;
            })}
            </div>
          </div>

          {}
          <div ref={includedRef} className="lg:relative">
            <div className="lg:sticky lg:top-24">
              <Card className={`bg-card/60 backdrop-blur-md border border-border shadow-lg py-8 px-7 gap-5 transition-all duration-700 ${includedVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-8"}`}>
                <div className="flex items-center gap-3 pb-4 border-b border-border">
                  <Radio className="size-5 text-secondary" strokeWidth={1.5} aria-hidden="true" />
                  <p className="font-mono text-xs tracking-[0.25em] text-secondary uppercase">
                    Deliverables
                  </p>
                </div>

                <h3 className="font-default font-bold text-2xl uppercase tracking-tight text-foreground">
                  What's Included
                </h3>

                <p className="font-default text-sm text-muted-foreground leading-relaxed">
                  Every Infinix engagement is fully scoped and transparent. No hidden costs.
                </p>

                <ul className="flex flex-col gap-4 mt-2">
                  {included.map((item, i) => <li data-index={i} key={item} className="flex items-start gap-3">
                      <Checkbox checked disabled className="mt-0.5 size-5 border-secondary data-[state=checked]:bg-secondary data-[state=checked]:text-secondary-foreground data-[state=checked]:border-secondary disabled:opacity-100 disabled:cursor-default" aria-label={item} />
                      <span className="font-default text-sm text-foreground leading-snug">
                        {item}
                      </span>
                    </li>)}
                </ul>

                <div className="mt-4 pt-5 border-t border-border">
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs tracking-widest text-muted-foreground uppercase">
                      Status
                    </span>
                    <span className="flex items-center gap-2 font-mono text-xs tracking-widest text-secondary uppercase">
                      <span className="size-2 bg-secondary animate-pulse" aria-hidden="true" />
                      Active
                    </span>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>;
}